import type { PageRouteInfo } from "@agent-ui/features/chat/utils/pageRouteExtraction";

import { useEffect, useRef } from "react";

import { useSessionUuid } from "@agent-ui/features/chat/hooks/useSessionUuid";
import { useSessionStoreActions } from "@agent-ui/features/chat/store/SessionStoreContext";
import { extractPageRouteFromToolPart } from "@agent-ui/features/chat/utils/pageRouteExtraction";
import { usePageTabsActions } from "@agent-ui/features/page-renderer/store/pageTabsStore";
import { useSandboxNavigate } from "@agent-ui/store/sandboxNavigationStore";
import { skipToken, useMutation } from "@tanstack/react-query";
import { useReploAgent } from "@v2/contexts/ReploAgentContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { opencodeRouter } from "@v2/trpc/routes/opencode";

/**
 * Subscribes the DO WebSocket connection to forward live events for a session
 * if it's not already subscribed. Uses the synced `streamingSessionState` from
 * the DO as the source of truth — if the sessionUuid is present as a key, the
 * connection is already subscribed. This naturally handles WebSocket reconnection:
 * after a reconnect, `streamingSessionState` is rebuilt from the new connection's
 * subscriptions (empty), so this will re-subscribe automatically.
 *
 * @author Noah 2026-03-04, REPL-24679
 */
function useSubscribeToStreamingSession({
  sessionUuid,
  agentSessionId,
}: {
  sessionUuid: string | null;
  agentSessionId: string | null;
}) {
  const { state, actions: agentActions } = useReploAgent();

  const isSubscribed =
    sessionUuid != null &&
    state.streamingSessionState?.[sessionUuid] !== undefined;

  const streamMessagesMutation = useMutation({
    mutationFn: async (params: { sessionId: string; sessionUuid: string }) => {
      await agentActions.streamMessages(params);
    },
  });

  useEffect(() => {
    if (!sessionUuid || !agentSessionId || isSubscribed) {
      return;
    }

    streamMessagesMutation.mutate({
      sessionId: agentSessionId,
      sessionUuid,
    });
  }, [
    sessionUuid,
    agentSessionId,
    isSubscribed,
    streamMessagesMutation.mutate,
  ]);
}

/**
 * Fetches historical messages for an existing OpenCode session from the DB and
 * hydrates them into the session store via addOrUpdate (upsert) actions.
 *
 * This safely races with useOpencodeEventRouter. For example: the user reopens
 * a session, and the DB fetch starts. While the fetch is in-flight, a real-time
 * event arrives via WebSocket and writes message M with finishReason="stop" into
 * the store. Then the DB fetch completes with an older snapshot of M where
 * finishReason is still null. The addOrUpdate actions compare updatedAt
 * timestamps and skip the write if the store already holds a newer version, so
 * the real-time data is preserved.
 *
 * Also registers the session (maps sessionUuid → agentSessionId) so that the
 * event router knows where to route incoming real-time events (this session is
 * registered either when creating the session, in createSession, but here we
 * shouldn't call that, as we're not creating a session, we're resuming it).
 *
 * After hydrating messages, restores page tabs and preview navigation from
 * historical tool parts (write/edit on page.tsx files). This ensures the
 * preview pane shows the relevant page(s) when reopening a session.
 *
 * The route returns { sessionId: null, messages: [] }
 * when the session isn't in the DB yet — this happens for new sessions because
 * the frontend sets sessionUuid in the URL before the sessionCreate RPC
 * persists the session. We skip messages fetching in that case since messages for new
 * sessions arrive via WebSocket: there's no historical messages to load (as session
 * doesn't exist in DB yet).
 *
 * @author Max 2026-02-18
 */
export function useLoadHistoricalMessages() {
  const sessionUuid = useSessionUuid();
  const projectId = useCurrentProjectId();

  const actions = useSessionStoreActions();
  const { openTab } = usePageTabsActions();
  const sandboxNavigate = useSandboxNavigate();
  const hasRestoredTabsRef = useRef(false);
  const previousSessionUuidRef = useRef(sessionUuid);

  // NOTE (Max, 2026-03-03): Ensures when the sessionUuid changes (user clicked on a different chat), the hasRestoredTabsRef is reset to false,
  // so that the tabs are restored again for that new chat.
  if (previousSessionUuidRef.current !== sessionUuid) {
    previousSessionUuidRef.current = sessionUuid;
    hasRestoredTabsRef.current = false;
  }

  const { data, isLoading, hasNextPage, fetchNextPage } =
    opencodeRouter.findPaginatedMessagesBySessionUuid.useInfiniteQuery(
      sessionUuid && projectId ? { sessionUuid, projectId } : skipToken,
      {
        getNextPageParam: ({ nextCursor }) => nextCursor,
      },
    );

  const sessionId = data?.pages[0]?.sessionId;

  // NOTE (Noah, 2026-03-04, REPL-24679): Subscribe the DO WebSocket connection to
  // forward live events for this session. Without this, navigating directly to a chat
  // URL (deep-linking) would load historical messages but never receive real-time
  // updates like preview URLs or streaming events.
  useSubscribeToStreamingSession({
    sessionUuid,
    agentSessionId: sessionId ?? null,
  });

  useEffect(() => {
    if (!data?.pages || !sessionUuid) {
      return;
    }

    let lastExtractedPageRoute: PageRouteInfo | null = null;

    // NOTE (Max, 2026-03-03): Every time a new page is fetched, we re-process
    // all pages (including previously hydrated ones) rather than tracking which
    // pages are new. This is safe because addOrUpdateMessage/addOrUpdateMessagePart
    // compare updatedAt timestamps and skip writes for already-seen or stale data.
    for (const page of data.pages) {
      if (page.sessionId) {
        actions.registerSession({
          sessionUuid,
          agentSessionId: page.sessionId,
        });
      }

      for (const { message, parts } of page.messages) {
        actions.addOrUpdateMessage({ sessionUuid, message });
        for (const part of parts) {
          actions.addOrUpdateMessagePart({
            sessionUuid,
            messageOpencodeId: message.opencodeId,
            part,
          });

          // NOTE (Max, 2026-03-02): Only restore tabs on the first run.
          // sandboxNavigate depends on useSearchParams, which makes it
          // referentially unstable — without this guard the effect re-fires
          // on every URL change and overwrites the user's tab navigation.
          // This also prevents re-opening tabs when pagination fetches new pages.
          if (!hasRestoredTabsRef.current) {
            const extractedPageRoute = extractPageRouteFromToolPart(part);
            // NOTE (Max, 2026-03-03): Open a new tab for the page that was written/edited, if any
            if (extractedPageRoute) {
              const { route, toolAction } = extractedPageRoute;
              openTab({
                id: route,
                content: {
                  type: "page",
                  pageRoute: route,
                  status: toolAction === "write" ? "new" : "edited",
                },
              });
              lastExtractedPageRoute = extractedPageRoute;
            }
          }
        }
      }
    }

    // NOTE (Max, 2026-03-03): Navigate to the last extracted page route (the latest edited page from the latest historical message)
    if (!hasRestoredTabsRef.current && lastExtractedPageRoute) {
      sandboxNavigate(lastExtractedPageRoute.route);
    }
    hasRestoredTabsRef.current = true;
  }, [data, sessionUuid, actions, openTab, sandboxNavigate]);

  return { isLoading, hasNextPage: hasNextPage ?? false, fetchNextPage };
}
